import React, { useState, useEffect } from 'react';

import { companyApis } from '@apis/companyApis';
import loadingImg from '@assets/img/arts/loading.svg';
import d2dLogo from '@assets/img/icons/d2d-white.png';
import { Breadcrumb } from '@components/breadcrumbs/Breadcrumb';
import { Button } from '@components/Button';
import { CookiePolicy } from '@components/CookiePolicy';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { LoadingErrorsFallback } from '@components/LoadingErrorsFallback';
import { MemberIntroDialog } from '@components/MemberIntroDialog';
import { SidebarListItem } from '@components/SidebarListItem';
import { UserMenu } from '@components/UserMenu';
import { QingdaoAlert } from '@components/utilities/Dashboard/QingdaoAlert';
import { useLogout } from '@hooks/mutation/useLogout';
import { useMaintenanceRedirect } from '@hooks/next/useMaintenanceRedirect';
import { useCompany } from '@hooks/queries/useCompany';
import { useUser } from '@hooks/queries/useUser';
import { useOpenInVscode } from '@hooks/useOpenInVscode';
import { useTracking } from '@hooks/useTracking';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItemText from '@material-ui/core/ListItemText';
import Dehaze from '@material-ui/icons/Dehaze';
import { AuthManager } from '@store/AuthManager';
import { type ClassMap, makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createStrings } from '@utils/local';
import { MetaHelper, url } from '@utils/pages';
import type { AppUrl } from '@utils/pages';
import clsx from 'clsx';
import Color from 'color';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { Link, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    copyrights: `(C) Copyright ${new Date().getFullYear()} FC STANDARD LOGISTICS. All rights reserved.`,
    helpCenter: 'ヘルプセンター',
    privacyPolicy: 'プライバシーポリシー',
    cookiePolicy: 'クッキーポリシー',
    termsOfUse: '利用規約',
    aboutCompany: '運営会社',
    applyForMembership: 'D2D MEMBERS 申込',
    pendingMembership: 'D2D MEMBERS 開設中',
    dashboard: 'ダッシュボード',
    priceSimulator: '料金シミュレーター',
    inquiry: '問い合わせ',
    bookingInquiry: '予約申込',
    bookingManagement: '案件管理',
    cargoTracking: '貨物追跡',
    invoice: '請求書',
    toManagementSystem: '管理システムへ',
    becomeD2dMemberNow: '今すぐD2D MEMBERSになる',
    login: 'ログイン',
    register: 'アケウント作成',
    networkErrorPrompt: () => (
      <>
        申し訳ございませんが、読み込み中にエラーが発生しました。
        <br />
        <br />
        ネットワークを確認し、ページを更新してみてください。
        <br />
        <br />
        問題が解決しない場合は、
        <a href="https://fcsl.force.com/d2d/s/" target="_blank" rel="noreferrer">
          サポート
        </a>
        にお問い合わせください。
      </>
    ),
    refresh: '再読込',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    ...common.flexColumn,
    background: theme.palette.background.default,
    minHeight: '100vh',
    '&$screenHeight': { height: '100vh' },
  },
  logo: {
    display: 'block',
    height: 23,
    width: 100,
  },
  headerContainer: {
    backgroundColor: theme.palette.primary.main,
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 1100,
  },
  headerInner: {
    ...common.flexInline,
    alignItems: 'center',
    margin: '0 auto',
    padding: '0 1rem',
    maxWidth: '1600px',
    height: 70,
  },
  sidebarContainer: {
    backgroundColor: theme.palette.grey['900'],
    color: theme.palette.common.white,
  },
  cookieAgreementContainer: {
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: '100%',
    ...common.flexColumn,
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: theme.palette.common.white,
    height: 140,
  },
  closeBtn: {
    position: 'absolute',
    right: '15px',
    top: '15px',
    width: '10px',
    height: '10px',
    cursor: 'pointer',
  },
  applyForCookieAgreement: {
    padding: '12px 43px',
  },
  footerContainer: {
    ...common.flexColumn,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    flexShrink: 0,
    minHeight: 160,
  },
  linkContainer: {
    ...common.flexInline,
    width: 'auto',
    ':root & $linkItem:not(:last-child)': {
      borderRightWidth: 1,
      borderRightStyle: 'solid',
      borderRightColor: theme.palette.common.white,
      [theme.breakpoints.down('sm')]: {
        border: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      ...common.flexColumn,
      paddingTop: 15,
      paddingBottom: 15,
      rowGap: '1rem',
    },
  },
  linkItem: {
    ...common.baseButton,
    ...common.middleText,
    textAlign: 'center',
    padding: '0 2rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  split: {
    height: 1,
    margin: '0 auto',
    backgroundColor: theme.palette.common.white,
    opacity: 0.1,
    maxWidth: '70rem',
  },
  loading: {
    ...common.flexCenter,
    flex: 1,
  },
  errors: {
    ...common.flexColumnCenter,
    ...common.normalText,
    ...common.fontWeight500,
    ...common.textGray800,
    flex: 1,
    '& a': {
      ...common.textPrimary,
      textDecoration: 'underline',
    },
  },
  errorTextContainer: {
    textAlign: 'center',
  },
  errorLogo: {
    height: 52 * 0.9,
    width: 182 * 0.9,
    marginBottom: '2rem',
  },
  mainContainer: {
    ...common.flexColumn,
    flex: 1,
    '$screenHeight &': {
      flexShrink: 1,
      overflow: 'hidden',
    },
  },
  headerLink: {
    ...common.middleText,
    color: theme.palette.common.white,
    bottom: 0,
    left: 0,
    '&+&': {
      left: '7rem',
    },
    position: 'static',
    marginLeft: '2rem',
    padding: 0,
    '&$disabled': {
      color: Color(theme.palette.primary.contrastText).alpha(0.5).toString(),
      cursor: 'not-allowed',
    },
  },
  headerButton: {
    bottom: '0.5rem',
    right: '1rem',
  },
  copyrightsText: {
    ...common.middleText,
    opacity: 0.5,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '1rem',
    },
  },
  breadcrumbOuter: {
    position: 'sticky',
    top: 70,
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  },
  breadcrumbInner: {
    maxWidth: 1600,
    margin: '14px auto',
    padding: '0px 1rem',
  },
  disabled: {},
  screenHeight: {},
}));

interface LayoutProps {
  title: string;
  loading?: boolean;
  errors?: unknown[];
  children?: React.ReactNode | (() => React.ReactNode);
  hideHeader?: boolean;
  hideFooter?: boolean;
  hideCookiePolicy?: boolean;
  scrollTopTop?: boolean;
  classes?: ClassMap<'background'>;
  breadcrumbs?: (AppUrl | { text: string; to?: AppUrl })[];
  screenHeight?: boolean;
}

export const Layout: React.FC<LayoutProps> = observer((props) => {
  const {
    title,
    loading,
    errors = [],
    children = null,
    hideHeader = false,
    hideFooter = false,
    hideCookiePolicy = false,
    scrollTopTop = true,
    classes: customClasses = {},
    breadcrumbs,
    screenHeight,
  } = props;

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const authManager = AuthManager.shared;
  const logout = useLogout();

  const hasErrors = errors.some((e) => !!e);

  useMaintenanceRedirect();

  const user = useUser(undefined, {
    enabled: authManager.isLoggedIn,
  });
  const company = useCompany(undefined, {
    enabled: authManager.isLoggedIn,
  });

  const history = useHistory();

  const isStaff = user.data?.is_staff || user.data?.is_superuser;
  const isMember = company.data?.status === '手続き完了';
  const hasApplied = company.data?.status !== 'ドラフト';

  useEffect(() => {
    console.info(`${title} ${location.toString()} rendered at: ${window.performance?.now()}`);
    if (authManager.isLoggedIn) {
      companyApis.post('/company/refresh_grade/', {});
    }
  }, []);

  if (import.meta.env.DEV) {
    useOpenInVscode();
  }

  if (import.meta.env.VITE_APP_STAGE === 'production') {
    useTracking('UA-186066198-3');
  }

  useEffect(() => {
    if (scrollTopTop) {
      window.scrollTo({ top: 0 });
    }
  }, [scrollTopTop]);

  useEffect(() => {
    if (
      user.isSuccess &&
      user.data &&
      !user.data.email_verified &&
      import.meta.env.VITE_APP_STAGE === 'production' &&
      authManager.isLoggedIn
    ) {
      logout.mutateAsync();
      history.push(`${url('/auth/Login')}?emailChanged=true`);
    }
  }, [user.data, user.isSuccess]);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [openSidebar, setOpenSidebar] = useState(false);

  const isMobile = useIsMobile();

  const renderContent = () => {
    if (children instanceof Function) {
      return children();
    }
    return children;
  };

  const renderLoading = () => {
    return (
      <div className={classes.loading}>
        <img src={loadingImg} alt="Loading..." />
      </div>
    );
  };

  const renderHeaderUpMd = () => {
    return (
      <div className={classes.headerContainer}>
        <div className={classes.headerInner}>
          <a href={authManager.isLoggedIn ? '/utilities/Dashboard' : '/auth/Login'}>
            <img src={d2dLogo} className={classes.logo} alt="D2D Logo" />
          </a>

          {isStaff && (
            <Link to={url('/admin/booking/Dashboard')}>
              <Button
                color="primary"
                size="small"
                shape="round"
                text={strings.toManagementSystem}
                style={{ marginLeft: '2rem' }}
              />
            </Link>
          )}

          {company.data && (
            <>
              <Link
                to={isMember ? url('/utilities/BookingInquiry/InquiryForm') : '#'}
                className={clsx(classes.headerLink, !isMember && classes.disabled)}
              >
                {strings.bookingInquiry}
              </Link>

              <Link
                to={isMember ? url('/utilities/Bookings/CurrentBookings') : '#'}
                className={clsx(classes.headerLink, !isMember && classes.disabled)}
              >
                {strings.bookingManagement}
              </Link>

              <Link
                to={isMember ? url('/utilities/CargoTracking/CargoTracking') : '#'}
                className={clsx(classes.headerLink, !isMember && classes.disabled)}
              >
                {strings.cargoTracking}
              </Link>

              <Link
                to={isMember ? url('/utilities/Invoice/InvoiceList') : '#'}
                className={clsx(classes.headerLink, !isMember && classes.disabled)}
              >
                {strings.invoice}
              </Link>
            </>
          )}

          <div style={{ flex: 1 }} />

          {AuthManager.shared.isLoggedIn && (
            <>
              {company.data && !isMember && (
                <>
                  {!hasApplied && (
                    <Button
                      text={strings.becomeD2dMemberNow}
                      color="accent"
                      size="small"
                      onClick={() => setIsDialogOpen(true)}
                    />
                  )}
                  <HorizontalSpace width={16} />
                  <a
                    href="https://go.d2dship.com/l/932623/2021-11-03/dr69"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      text={strings.inquiry}
                      color="primary"
                      size="small"
                      style={{
                        paddingLeft: 24,
                        paddingRight: 24,
                      }}
                    />
                  </a>
                </>
              )}
              <HorizontalSpace width={16} />
              <UserMenu />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderHeaderDownMd = () => {
    return (
      <>
        <div className={classes.headerContainer}>
          <div className={classes.headerInner}>
            <div style={{ flex: 1, color: '#fff' }}>
              <IconButton
                aria-label="open sidebar"
                color="inherit"
                onClick={() => {
                  setOpenSidebar(true);
                }}
              >
                <Dehaze />
              </IconButton>
            </div>
            <a href={authManager.isLoggedIn ? '/utilities/Dashboard' : '/auth/Login'}>
              <img src={d2dLogo} className={classes.logo} alt="D2D Logo" />
            </a>
            <div style={{ flex: 1 }} />
          </div>
        </div>
        <Drawer
          classes={{ paper: classes.sidebarContainer }}
          open={openSidebar}
          onClose={() => {
            setOpenSidebar(false);
          }}
        >
          <List>
            {AuthManager.shared.isLoggedIn && (
              <SidebarListItem to={url('/utilities/Dashboard')}>
                <ListItemText primary={strings.dashboard} />
              </SidebarListItem>
            )}
            {AuthManager.shared.isLoggedIn && (
              <SidebarListItem to={url('/utilities/PriceSimulator/PriceSimulator')}>
                <ListItemText primary={strings.priceSimulator} />
              </SidebarListItem>
            )}

            {AuthManager.shared.isLoggedIn && <UserMenu />}

            {!AuthManager.shared.isLoggedIn && (
              <>
                <SidebarListItem to={url('/auth/Login')}>{strings.login}</SidebarListItem>

                <SidebarListItem to={url('/auth/CreateAccount')}>
                  {strings.register}
                </SidebarListItem>
              </>
            )}
          </List>
        </Drawer>
      </>
    );
  };

  const renderHeader = () => (isMobile ? renderHeaderDownMd() : renderHeaderUpMd());

  const renderFooter = () => (
    <div className={classes.footerContainer}>
      <div className={classes.flexCenter} style={{ flex: 1 }}>
        <div className={classes.linkContainer}>
          <div className={classes.linkItem}>
            <a href="https://fcsl.force.com/d2d/s/" target="_blank" rel="noreferrer">
              {strings.helpCenter}
            </a>
          </div>
          <div className={classes.linkItem}>
            <Link to={url('/about/PrivacyPolicy')}>{strings.privacyPolicy}</Link>
          </div>
          <div className={classes.linkItem}>
            <Link to={url('/about/CookiePolicy')}>{strings.cookiePolicy}</Link>
          </div>
          <div className={classes.linkItem}>
            <Link to={url('/about/TermsOfUse')}>{strings.termsOfUse}</Link>
          </div>
          <div className={classes.linkItem}>
            <Link to={url('/about/AboutCompany')}>{strings.aboutCompany}</Link>
          </div>

          {import.meta.env.DEV && (
            <div className={classes.linkItem}>
              <Link to={url('/dev/Accounts')}>DEV</Link>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className={classes.split} />
      </div>
      <div className={classes.flexCenter} style={{ flex: 1 }}>
        <span
          className={classes.copyrightsText}
          onClick={import.meta.env.DEV ? () => history.push(url('/dev/Urls')) : undefined}
        >
          {strings.copyrights}
        </span>
      </div>
    </div>
  );

  const renderBreadcrumbs = () => {
    return (
      <div className={classes.breadcrumbOuter}>
        <div className={classes.breadcrumbInner}>
          {breadcrumbs && (
            <Breadcrumb
              bottomLineHidden
              items={breadcrumbs.map((to, i) =>
                typeof to === 'string'
                  ? {
                      to: i !== breadcrumbs.length - 1 ? to : undefined,
                      text: MetaHelper.shared.getPageMeta(to)!.title!,
                    }
                  : to,
              )}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={clsx(
        classes.background,
        customClasses.background,
        screenHeight && classes.screenHeight,
      )}
    >
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
      </Helmet>

      <MemberIntroDialog isOpen={isDialogOpen} setIsOpen={setIsDialogOpen} />

      {!hideHeader && renderHeader()}
      {breadcrumbs && renderBreadcrumbs()}
      <div className={classes.mainContainer}>
        {(loading || logout.isLoading) && !hasErrors ? (
          renderLoading()
        ) : (
          <LoadingErrorsFallback prompt={strings.networkErrorPrompt()} errors={errors}>
            {renderContent}
          </LoadingErrorsFallback>
        )}
      </div>
      {!hideFooter && renderFooter()}
      {!hideCookiePolicy && <CookiePolicy />}

      {import.meta.env.VITE_APP_STAGE !== 'e2e' && authManager.isLoggedIn && <QingdaoAlert />}
    </div>
  );
});
